import { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './stepInfo.module.scss';
import { TextField, MenuItem } from '@mui/material';
import { StepInfoProps } from '../../../interfaces/salePost';
import { capitalize, getYears, formatInput, addPointsInText } from "../../../helpers/app";
import Button from "../../Button";
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { images } from '../../../constants/images';
import CustomFileUploader from '../../CustomFileUploader/CustomFileUploader';
import { ImageFile } from '../../../interfaces/salePost';
import { uploaderImages } from '../../../constants/uploaderImages';

const selectProps = {
    MenuProps: {
        PaperProps: {
            style: { maxHeight: 200 }
        },
        anchorOrigin: {
            vertical: "bottom" as const,
            horizontal: "left" as const
        },
        transformOrigin: {
            vertical: "top" as const,
            horizontal: "left" as const
        }
    }
};


const sxTextFieldDefault = {
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: 'black',
      },
      '&:hover fieldset': {
        borderColor: 'black',
      },
    },
    '& .MuiInputLabel-root': {
      '&.Mui-focused': {
        color: 'black',
      },
    },
  };
  


const StepInfo = ({
    user,
    adminBrand,
    onContinue,
    getBrands,
    cleanGetBrands,
    setBrand,
    getModels,
    cleanGetModels,
    models,
    setModel,
    brands,
    onValidityChange,
    onVersionsChange,
    cantidadVersiones,
    postPublicationsBrandImages,
    onMainImageChange,
    publication
}: StepInfoProps) => {
    const location = useLocation();
    const [hasInitialized, setHasInitialized] = useState(false);
    const [disabled, setDisabled] = useState<boolean>(true);
    const [modelSelected, setModelSelected] = useState<any>({ id: null, name: null });
    const [brandSelected, setBrandSelected] = useState<any>({ id: null, name: null });
    const [disabledNewCar, setDisabledNewCar] = useState<boolean>(true);
    const isEditing = !!publication?.data;

    const customIconStyle = {
        width: 10,
        height: 10,
        border: '1px solid green',
        padding: 0.5,
        borderRadius: '50%',
        display: 'flex',
        marginRight: 2,
        alignItems: 'center',
        justifyContent: 'center'
    };
    const [formData, setFormData] = useState({
        marca: '',
        modelo: '',
        cantidadVersiones: 1,
        imagen: '',
        año: '',
    });
    
    const [mainImage, setMainImage] = useState<any>(null);
    const [imagesUploading, setImagesUploading] = useState(false);
    const isUpdatingFromParent = useRef(false);


    useEffect(() => {
        if (getBrands) {
            getBrands();
        }
    }, []);

    useEffect(() => {
        if (cantidadVersiones !== undefined && cantidadVersiones !== formData.cantidadVersiones) {
            setFormData(prev => ({ ...prev, cantidadVersiones }));
        }
    }, [cantidadVersiones]);

     const brandsNormalized: any = useMemo(() => {
        if (!brands?.data) return [];
        
        
        const uniqueBrands = brands.data
            .filter((brand: any) => brand?.brand_id && brand?.name)
            .map((brand: any) => ({
                id: brand.brand_id,
                name: brand.name
            }))
            .filter((brand: any, index: number, self: any[]) => 
                index === self.findIndex((b: any) => b.id === brand.id)
            );
            
        return uniqueBrands;
    }, [brands?.data]);

    
    useEffect(() => {
        if (brandSelected?.id && brandsNormalized?.length > 0) {
            const brandExists = brandsNormalized.some((brand: any) => brand.id === brandSelected.id);
            if (!brandExists) {
                setBrandSelected({ id: null, name: null });
                setFormData(prev => ({ ...prev, marca: '' }));
            }
        }
    }, [brandsNormalized, brandSelected?.id]);

    
    useEffect(() => {
        if (adminBrand?.data?.brands?.length > 0 && brandsNormalized?.length > 0) {
           
            const userAdminBrand = adminBrand.data.brands.find((brand: any) => 
                brand._id === user?.brand || brand.brand_id === user?.brand
            );
            
            if (userAdminBrand) {
                const adminBrandName = userAdminBrand.name;
                const matchingBrand = brandsNormalized.find((brand: any) => 
                    brand.name.toLowerCase() === adminBrandName.toLowerCase()
                );
                
                if (matchingBrand && !brandSelected?.id) {
                    setBrandSelected(matchingBrand);
                    setFormData(prev => ({ ...prev, marca: matchingBrand.name }));
                    
                    
                    if (getModels) {
                        getModels(matchingBrand.id.toString());
                    }
                }
            }
        }
    }, [adminBrand?.data?.brands, brandsNormalized, brandSelected?.id, user?.brand, setBrandSelected, setFormData, getModels]);

    const modelsNormalized: any = useMemo(() => {
        if (!models?.data) return [];
 
        const uniqueModels = models.data
            .filter((model: any) => model?.model_id && model?.name)
            .map((model: any) => ({
                id: model.model_id,
                name: model.name
            }))
            .filter((model: any, index: number, self: any[]) => 
                index === self.findIndex((m: any) => m.id === model.id)
            );
            
        return uniqueModels;
    }, [models?.data]);

    
    useEffect(() => {
        if (modelSelected?.id && modelsNormalized?.length > 0) {
            const modelExists = modelsNormalized.some((model: any) => model.id === modelSelected.id);
            if (!modelExists) {
                setModelSelected({ id: null, name: null });
                setFormData(prev => ({ ...prev, modelo: '' }));
            }
        }
    }, [modelsNormalized, modelSelected?.id]);


    useEffect(() => {
        if (cantidadVersiones && cantidadVersiones !== formData.cantidadVersiones) {
            isUpdatingFromParent.current = true;
            setFormData(prev => ({
                ...prev,
                cantidadVersiones: cantidadVersiones
            }));

            setTimeout(() => {
                isUpdatingFromParent.current = false;
            }, 0);
        }
    }, [cantidadVersiones]);

    useEffect(() => {
        if (publication?.data && !hasInitialized) {
          setHasInitialized(true);
      
          const modelName = publication.data.vehicles?.[0]?.model;
          const cantidad = publication.data.vehicles?.length || 1;
          const mainPicture = publication.data.pictures?.[0];
      
          setFormData(prev => ({
            ...prev,
            cantidadVersiones: cantidad
          }));
      
          if (onVersionsChange) onVersionsChange(cantidad);
      
          if (modelsNormalized.length && modelName) {
            const matchModel = modelsNormalized.find((m: any) => m.name.toLowerCase() === modelName.toLowerCase());
            if (matchModel) {
              setModelSelected({ id: matchModel.id, name: matchModel.name });
              setFormData(prev => ({ ...prev, modelo: matchModel.name }));
              if (setModel) setModel({ id: matchModel.id, label: matchModel.name });
            }
          }
      
          if (mainPicture?.id && mainPicture?.path) {
            const preloadedImage = {
              id: mainPicture.id,
              url: mainPicture.path,
              previewURL: mainPicture.path,
              upload: "success",
              uploaded: true
            };
            setMainImage(preloadedImage);
          }
        }
      }, [publication?.data, modelsNormalized]);
      


    useEffect(() => {
        const brandAvailable = brandSelected?.id;
        const modelAvailable = modelSelected?.id;
        
        const hasValidMainImage = mainImage && (
            typeof mainImage.id === 'string' && 
            mainImage.id.length > 0 &&
            mainImage.uploaded === true &&
            mainImage.upload === "success"
        );
        
        const requiredFieldsValid = (
            brandAvailable &&
            modelAvailable &&
            hasValidMainImage &&
            formData.cantidadVersiones >= 1
        );
        
        setDisabled(!requiredFieldsValid);
        setDisabledNewCar(!requiredFieldsValid);
        
        if (onValidityChange) {
            onValidityChange(requiredFieldsValid);
        }

        if (onVersionsChange && !isUpdatingFromParent.current) {
            onVersionsChange(formData.cantidadVersiones);
        }
    }, [
        brandSelected?.id,
        modelSelected?.id,
        mainImage,
        formData.cantidadVersiones,
        onValidityChange,
        onVersionsChange
    ]);


    const handleBrandChange = (brandId: number) => {
        const selectedBrand = brandsNormalized?.find((brand: any) => brand.id === brandId);

        if (selectedBrand) {
            setBrandSelected(selectedBrand);
            setFormData(prev => ({
                ...prev,
                marca: selectedBrand.name,
                modelo: "",
                version: ""
            }));

            if (cleanGetModels) cleanGetModels();
            setModelSelected({ id: null, name: null });

            if (setBrand) setBrand({ id: selectedBrand.id, label: selectedBrand.name });

        
            if (getModels) getModels(brandId.toString());
        }
    };


    const handleModelChange = (modelId: number) => {
        const selectedModel = modelsNormalized?.find((model: any) => model.id === modelId);

        if (selectedModel) {
            setModelSelected(selectedModel);
            setFormData(prev => ({ ...prev, modelo: selectedModel.name, version: "" }));

            if (setModel) setModel({ id: selectedModel.id, label: selectedModel.name });
            
            validateForm();
        }
    };
    

    const validateForm = () => {
        let hasValidMainImage = false;
        
        if (mainImage) {
            
            hasValidMainImage = (
                typeof mainImage.id === 'string' && 
                mainImage.id.length > 0 &&
                mainImage.uploaded === true &&
                mainImage.upload === "success"
            );
        }
        
        const hasRequiredFields = !!modelSelected?.id && !!brandSelected?.id;
        const isValid = hasRequiredFields && hasValidMainImage;
        
       
        setDisabledNewCar(!isValid);
        
        onValidityChange && onValidityChange(isValid);
    };
    
    const handleImageUpload = async (files: ImageFile[]) => {
        if (!files.length) return;
        
        setImagesUploading(true);
        
        try {
            if (postPublicationsBrandImages && files[0].file) {
                const formData = new FormData();
                formData.append('file', files[0].file);
                
                const response = await postPublicationsBrandImages(formData, 0);

                
                
                if (response && response.data && response.data.file && response.data.file.id) {
                  
                    
                    const uploadedImage = {
                        ...files[0],
                        id: response.data.file.id,
                        url: response.data.file.path,
                        previewURL: response.data.file.path,
                        upload: "success",
                        uploaded: true
                    };
                    
                    setMainImage(uploadedImage);

                    if (onMainImageChange) onMainImageChange(uploadedImage);

                    const hasValidImage = (
                        typeof uploadedImage.id === 'string' && 
                        uploadedImage.id.length > 0 &&
                        uploadedImage.uploaded === true &&
                        uploadedImage.upload === "success"
                    );
                    
                    const isFormValid = !!modelSelected?.id && !!brandSelected?.id && hasValidImage;
                   
                    
                    setDisabledNewCar(!isFormValid);
                } else {
                    console.error('Estructura de respuesta inválida o falta el ID de la imagen:', response);
                }
            }
        } catch (error) {
            console.error("Error uploading image:", error);
        } finally {
            setImagesUploading(false);
        }
    };
    
    const handleDeleteImage = () => {
        setMainImage(null);
        if (onMainImageChange) onMainImageChange(null);
        validateForm();
    };

    return (
        <div className={styles.basicInfoContainer}>
            <div className={styles.formSection}>
                <div className={styles.alertMessage}>
                    <p>
                        Completa los campos con la información general del modelo y la cantidad de versiones que vas a publicar en este lote.
                    </p>
                </div>


                <div className={styles.formGrid}>
                    <TextField
                        select
                        required
                        label="Marca"
                        sx={sxTextFieldDefault}
                        value={(() => {
                           
                            const selectedValue = brandSelected?.id || '';
                            if (selectedValue && brandsNormalized?.some((brand: any) => brand.id === selectedValue)) {
                                return selectedValue;
                            }
                            return '';
                        })()}
                        onChange={(e) => handleBrandChange(parseInt(e.target.value))}
                        disabled={true}
                        className={styles.brandSelect}
                        SelectProps={{
                            ...selectProps,
                            IconComponent: (props) =>
                                brandSelected?.id ? (
                                    <Box sx={customIconStyle}>
                                        <CheckIcon sx={{ color: 'green', fontSize: 15 }} />
                                    </Box>
                                ) : (
                                    <ArrowDropDownIcon {...props} />
                                ),
                        }}
                    >
                        {brandsNormalized?.map((brand: any) => (
                            <MenuItem key={`brand-${brand.id}`} value={brand.id}>
                                {brand.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className={styles.twoColumnGrid}>
                    <TextField
                        select
                        required
                        label="Modelo"
                        sx={sxTextFieldDefault}
                        value={(() => {
                            const selectedValue = modelSelected?.id || '';
                            if (selectedValue && modelsNormalized?.some((model: any) => model.id === selectedValue)) {
                                return selectedValue;
                            }
                            return '';
                        })()}
                        onChange={(e) => handleModelChange(parseInt(e.target.value))}
                        disabled={!modelsNormalized || modelsNormalized.length === 0 || isEditing}
                        className={styles.modelSelect}
                        SelectProps={{
                            ...selectProps,
                            IconComponent: (props) =>
                                modelSelected?.id ? (
                                    <Box sx={customIconStyle}>
                                        <CheckIcon sx={{ color: 'green', fontSize: 15 }} />
                                    </Box>
                                ) : (
                                    <ArrowDropDownIcon {...props} />
                                ),
                        }}
                    >
                        {modelsNormalized?.map((model: any) => (
                            <MenuItem key={`model-${model.id}`} value={model.id}>
                                {model.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        type="number"
                        label="Cantidad de versiones"
                        sx={sxTextFieldDefault}
                        value={formData.cantidadVersiones || ''}
                        onChange={(e) => {
                            const value = Math.max(1, parseInt(e.target.value) || 1);
                            setFormData(prev => ({ ...prev, cantidadVersiones: value }));
                        }}
                        disabled={!modelSelected?.id || isEditing}
                        className={styles.versionInput}
                        InputProps={{
                            inputProps: { 
                                min: 1,
                                max: 50
                            }
                        }}
                    />
                </div>


                <p style={{color:'#0D0C0C', fontWeight:'400', fontSize:'16px'}}>Adjunta una foto del frente del vehículo, para utilizar como principal en la publicación.
                No incluyas bordes, logos, marcas de agua, contacto o textos promocionales. </p>
                        <div className={styles.uploaderItem}>
                                  <h3>Foto de frente </h3>
                                  <div className={styles.coverPhotoWrapper}>
                                    <CustomFileUploader
                                      values={mainImage ? [mainImage] : []}
                                      onChange={(files: ImageFile[]) => handleImageUpload(files)}
                                      onDeleteItem={handleDeleteImage}
                                      className={styles.fileUploader}
                                      placeholderImage={uploaderImages.front}
                                      loading={imagesUploading}
                                      deleting={false}
                                    />
                                    <div className={styles.coverBadge}>Portada</div>
                                  </div>
                                </div>
            </div>
            <div className={location.state?.type === 'new-car' ? styles.imageSectionNewCar : styles.imageSection}>
                <img src={images.linzeFlujoPublicacion} alt="Linze Keys" />
                <div className={styles.buttonContainer}>

                {!isEditing && (
                <Button
                    onPress={() => {
                    onContinue({
                        brandId: brandSelected.id,
                        brandName: brandSelected.name,
                        modelId: modelSelected.id,
                        modelName: modelSelected.name,
                        cantidadVersiones: formData.cantidadVersiones,
                        mainImage: mainImage ? [mainImage.id] : []
                    });
                    }}
                    title="Cargar versiones"
                    primary
                    disabled={disabledNewCar}
                    className={styles.submitButton}
                />
                )}
                </div>
            </div>
        </div>
    );
};

export default StepInfo;
